import { parseU64OrZero } from "@bridgesplit/utils";
import { ExternalYieldInfo } from "@bridgesplit/abf-sdk";

import {
    StrategyApi,
    StrategyTermsApi,
    StrategyInfoApi,
    StrategyInfoUsdFieldsApi,
    ParsedCapMonitorApi,
    AssetTermsApi,
    ApiStrategyInfoResponse
} from "../api/strategy";
import {
    Strategy,
    StrategyTerms,
    ParsedCapMonitor,
    StrategyInfo,
    StrategyInfoUsdFields,
    AssetTerms,
    StrategyInfoResponse
} from "../domain/strategy";
import { ExternalYieldInfoApi } from "../api/external-yield";
import { Mint } from "../alias";

function parseStrategyFromApi(api: StrategyApi): Strategy {
    return {
        id: api.id,
        address: api.address,
        version: api.version,
        nonce: api.nonce,
        bump: api.bump,
        principalMint: api.principalMint,
        tokenBalance: Number(api.tokenBalance),
        lender: api.lender,
        originationsEnabled: api.originationsEnabled,
        externalYieldSource: api.externalYieldSource,
        interestPerSecond: api.interestPerSecond,
        lastAccruedTimestamp: Number(api.lastAccruedTimestamp),
        liquidityBuffer: Number(api.liquidityBuffer),
        interestFee: Number(api.interestFee),
        principalFee: Number(api.principalFee),
        originationFee: Number(api.originationFee),
        originationCap: Number(api.originationCap),
        externalYieldAmount: Number(api.externalYieldAmount),
        currentDeployedAmount: Number(api.currentDeployedAmount),
        outstandingInterestAmount: Number(api.outstandingInterestAmount),
        feeClaimable: Number(api.feeClaimable),
        cumulativePrincipalOriginated: Number(api.cumulativePrincipalOriginated),
        cumulativeInterestAccrued: Number(api.cumulativeInterestAccrued),
        cumulativeLoanCount: Number(api.cumulativeLoanCount),
        activeLoanCount: Number(api.activeLoanCount),
        marketInformation: api.marketInformation,
        closed: api.closed,
        lastInteractedTime: Number(api.lastInteractedTime),
        lastInteractedTxn: api.lastInteractedTxn,
        createdAt: Number(api.createdAt),
        writeVersion: Number(api.writeVersion),
        supplyCap1hrPeriodStart: Number(api.supplyCap1hrPeriodStart),
        supplyCap24hrPeriodStart: Number(api.supplyCap24hrPeriodStart),
        supplyCap1hrAmount: Number(api.supplyCap1hrAmount),
        supplyCap24hrAmount: Number(api.supplyCap24hrAmount),
        borrowCap1hrPeriodStart: Number(api.borrowCap1hrPeriodStart),
        borrowCap24hrPeriodStart: Number(api.borrowCap24hrPeriodStart),
        borrowCap1hrAmount: Number(api.borrowCap1hrAmount),
        borrowCap24hrAmount: Number(api.borrowCap24hrAmount),
        withdrawCap1hrPeriodStart: Number(api.withdrawCap1hrPeriodStart),
        withdrawCap24hrPeriodStart: Number(api.withdrawCap24hrPeriodStart),
        withdrawCap1hrAmount: Number(api.withdrawCap1hrAmount),
        withdrawCap24hrAmount: Number(api.withdrawCap24hrAmount)
    };
}

function parseAssetTermsFromApi(api: AssetTermsApi): AssetTerms {
    return {
        durationAndApys: api.durationAndApys.map(([duration, apy]) => [duration, apy]),
        allocationInfo: {
            maxAllocationPct: parseU64OrZero(api.allocationInfo.maxAllocationPct),
            currentAllocationAmount: parseU64OrZero(api.allocationInfo.currentAllocationAmount)
        }
    };
}

function parseStrategyTermsFromApi(api: StrategyTermsApi): StrategyTerms {
    // Helper to convert caps
    function toCaps(src: { max1hr: string; max24hr: string; maxOutstanding: string }) {
        return {
            max1hr: parseU64OrZero(src.max1hr),
            max24hr: parseU64OrZero(src.max24hr),
            maxOutstanding: parseU64OrZero(src.maxOutstanding)
        };
    }

    const assetTerms: Record<Mint, AssetTerms> = {};
    for (const mint in api.assetTerms) {
        if (Object.prototype.hasOwnProperty.call(api.assetTerms, mint)) {
            assetTerms[mint] = parseAssetTermsFromApi(api.assetTerms[mint]);
        }
    }

    return {
        assetTerms,
        supplyCaps: toCaps(api.supplyCaps),
        borrowCaps: toCaps(api.borrowCaps),
        withdrawCaps: toCaps(api.withdrawCaps)
    };
}

// CapMonitorApi comes from api/strategy file, all fields = U64String
function parseCapMonitorFromApi(api: ParsedCapMonitorApi): ParsedCapMonitor {
    return {
        startTime1hr: Number(api.startTime1hr),
        startTime24hr: Number(api.startTime24hr),
        principal1hr: parseU64OrZero(api.principal1hr),
        principal24hr: parseU64OrZero(api.principal24hr)
    };
}

// If ExternalYieldInfoApi diverges from domain ExternalYieldInfo, write a converter here
function parseExternalYieldInfoFromApi(api?: ExternalYieldInfoApi): ExternalYieldInfo | undefined {
    if (!api) return undefined;
    return {
        externalYieldSource: api.externalYieldSource,
        balance: Number(api.balance), //TOOD: Bigints?
        apy: Number(api.apy), //TOOD: Bigints?
        yieldAccount: api.yieldAccount,
        lastUpdateTime: api.lastUpdateTime
    };
}

// USD/APY fields are f64 (not u64), so this is an explicit identity codec — kept as a dedicated parser
// so the same mapping serves both parseStrategyInfoFromApi and the inlined vault `strategySummary`.
export function parseStrategyInfoUsdFieldsFromApi(api: StrategyInfoUsdFieldsApi): StrategyInfoUsdFields {
    return {
        principalDeployedUsd: api.principalDeployedUsd,
        strategyBalanceUsd: api.strategyBalanceUsd,
        totalSupplyUsd: api.totalSupplyUsd,
        interestAccruedUsd: api.interestAccruedUsd,
        interestEarnedAllTimeUsd: api.interestEarnedAllTimeUsd,
        wAvgApy: api.wAvgApy,
        expected24hInterestUsd: api.expected24hInterestUsd
    };
}

export function parseStrategyInfoFromApi(api: StrategyInfoApi): StrategyInfo {
    return {
        strategy: parseStrategyFromApi(api.strategy),
        terms: parseStrategyTermsFromApi(api.terms),
        externalYieldInfo: parseExternalYieldInfoFromApi(api.externalYieldInfo),
        borrowCapMonitor: parseCapMonitorFromApi(api.borrowCapMonitor),
        supplyCapMonitor: parseCapMonitorFromApi(api.supplyCapMonitor),
        withdrawCapMonitor: parseCapMonitorFromApi(api.withdrawCapMonitor),
        ...parseStrategyInfoUsdFieldsFromApi(api)
    };
}

export function parseStrategyInfoResponseFromApi(api: ApiStrategyInfoResponse): StrategyInfoResponse {
    return {
        ...api,
        items: api.items.map(parseStrategyInfoFromApi)
    };
}
